import { useState, useEffect, useRef, useCallback, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ShoppingCart } from "lucide-react";
import type { FlashDeal, FlashDealProduct } from "../../hooks/useFlashDeals";
import { getSupabaseProductImageUrl } from "../../utils/imageUtils";

interface FlashDealsProps {
  deals: FlashDeal[];
  onAddToCart?: (product: FlashDealProduct) => void;
  onQuickView?: (product: FlashDealProduct) => void;
}

const FlashDeals: FC<FlashDealsProps> = ({ deals, onAddToCart }) => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const activeDeal = deals[0] as FlashDeal | undefined;

  // ── countdown ──────────────────────────────────────────────
  const [secondsLeft, setSecondsLeft] = useState(
    activeDeal?.seconds_remaining ?? 0,
  );

  useEffect(() => {
    setSecondsLeft(activeDeal?.seconds_remaining ?? 0);
  }, [activeDeal?.seconds_remaining]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = setInterval(
      () => setSecondsLeft((p) => (p > 0 ? p - 1 : 0)),
      1000,
    );
    return () => clearInterval(id);
  }, [secondsLeft > 0]);

  // ── scroll helpers ─────────────────────────────────────────
  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    return () => el.removeEventListener("scroll", updateScrollState);
  }, [updateScrollState]);

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.7;
    el.scrollBy({
      left: dir === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  // ── guard ──────────────────────────────────────────────────
  if (!activeDeal || activeDeal.products.length === 0) return null;

  const allProducts = deals.flatMap((d) => d.products);
  const hrs = Math.floor(secondsLeft / 3600);
  const min = Math.floor((secondsLeft % 3600) / 60);
  const sec = secondsLeft % 60;
  const pad = (n: number) => String(n).padStart(2, "0");

  const img = (p: FlashDealProduct) =>
    getSupabaseProductImageUrl(p.image) ??
    "https://via.placeholder.com/400x400";

  return (
    <section className="bg-primary py-8 md:py-10">
      <div className="max-w-7xl mx-auto px-4">
        {/* ─── HEADER ──────────────────────────────────────── */}
        <div className="flex items-center justify-between mb-5">
          {/* Title + countdown */}
          <div className="flex items-center gap-4 md:gap-6">
            <h2 className="text-xl md:text-2xl font-extrabold text-black">
              {activeDeal.name || "Today's Deals"}
            </h2>

            {/* Compact countdown */}
            <div className="hidden sm:flex items-center gap-1 text-sm font-mono">
              <span className="text-black/50 text-xs font-sans mr-1">
                Ends in
              </span>
              <span className="bg-black text-primary font-bold rounded px-2 py-1 tabular-nums text-xs">
                {pad(hrs)}
              </span>
              <span className="text-black/40 font-bold">:</span>
              <span className="bg-black text-primary font-bold rounded px-2 py-1 tabular-nums text-xs">
                {pad(min)}
              </span>
              <span className="text-black/40 font-bold">:</span>
              <span className="bg-black text-white font-bold rounded px-2 py-1 tabular-nums text-xs">
                {pad(sec)}
              </span>
            </div>
          </div>

          {/* Arrows */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              className="w-8 h-8 rounded-lg bg-black/10 hover:bg-black/20 flex items-center justify-center text-black/60 hover:text-black disabled:opacity-30 disabled:cursor-default transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              className="w-8 h-8 rounded-lg bg-black/10 hover:bg-black/20 flex items-center justify-center text-black/60 hover:text-black disabled:opacity-30 disabled:cursor-default transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ─── HORIZONTAL PRODUCT STRIP ────────────────────── */}
        <div className="relative">
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {allProducts.map((product) => {
              const original = product.display_price;
              const sale = product.display_sale_price;
              const pct =
                product.sale_rate > 0
                  ? Math.round(product.sale_rate)
                  : original > 0
                    ? Math.round(((original - sale) / original) * 100)
                    : 0;

              return (
                <div
                  key={product.id}
                  className="snap-start shrink-0 w-60 md:w-65 rounded-xl overflow-hidden bg-neutral-900 border border-neutral-800 hover:border-neutral-600 transition-all duration-300 group/card shadow-lg"
                >
                  {/* Image */}
                  <div
                    className="relative h-60 md:h-64 cursor-pointer overflow-hidden"
                    onClick={() => navigate(`/product/${product.id}`)}
                  >
                    <img
                      src={img(product)}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover/card:scale-105"
                      loading="lazy"
                    />

                    {/* Discount chip */}
                    {pct > 0 && (
                      <span className="absolute top-3 left-3 bg-white text-black text-[11px] font-bold px-2 py-0.5 rounded-md shadow">
                        -{pct}%
                      </span>
                    )}

                    {/* Bottom gradient with price */}
                    <div className="absolute inset-x-0 bottom-0 h-20 bg-linear-to-t from-black/80 via-black/40 to-transparent pointer-events-none" />
                    <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                      <div>
                        <span className="text-lg font-extrabold text-white drop-shadow-md">
                          ${sale.toFixed(2)}
                        </span>
                        {original > sale && (
                          <span className="ml-2 text-xs text-white/50 line-through">
                            ${original.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Bottom strip */}
                  <div className="px-3 py-2.5 flex items-center justify-between gap-2">
                    <h3
                      className="text-sm font-medium text-neutral-300 truncate cursor-pointer hover:text-white transition-colors flex-1"
                      onClick={() => navigate(`/product/${product.id}`)}
                    >
                      {product.name}
                    </h3>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddToCart?.(product);
                      }}
                      aria-label="Add to cart"
                      className="shrink-0 w-8 h-8 rounded-lg bg-neutral-800 hover:bg-primary text-neutral-400 hover:text-black flex items-center justify-center transition-all duration-200"
                    >
                      <ShoppingCart className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Fade edges — using primary tones to match the bg */}
          {canScrollLeft && (
            <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-linear-to-r from-primary to-transparent z-10" />
          )}
          {canScrollRight && (
            <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-linear-to-l from-primary to-transparent z-10" />
          )}
        </div>
      </div>
    </section>
  );
};

export default FlashDeals;
